import { useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  FormSectionLabel,
  ResponsiveGrid,
  Text,
} from "@marekl/adminer/components";
import { apiErrorMessage, notifyError, notifySuccess } from "@marekl/adminer/api";
import { useT } from "@marekl/adminer/hooks";
import {
  useCompetitionOffer,
  useUpdateCompetitionOffer,
  type Competition,
} from "../../../lib/queries/competitions";
import { familyName, useRuleSet, type Discipline } from "../../../lib/queries/rules";
import styles from "./OfferCard.module.css";
import { useCompetitionReadOnly } from "../../../shell/CompetitionLayout";
import type { ID } from "../../../lib/id";

/**
 * What the competition vypisuje: which disciplines run and which
 * performance classes they run in.
 *
 * Both belong to the whole SERIES rather than to this one event — the
 * rounds of a championship must offer the same thing or their results
 * are not comparable — so the card says how many competitions it speaks
 * for. A standalone pohárovka configures only itself and the sentence is
 * omitted.
 *
 * Disciplines are grouped by FAMILY, because that is how the rules book
 * is organized and how an organizer thinks: "we don't run twirling this
 * year" is one decision about six disciplines, not six decisions. Twenty
 * one checkboxes in one column made that decision six clicks long and
 * gave no sense of what was on.
 *
 * Classes are grouped BY CODE. A rule set holds one class row per class
 * table, so "A" exists several times over, while an organizer deciding
 * whether třída B runs is making one decision, not three.
 *
 * Everything freezes once the competition leaves draft: publishing
 * announces what runs, and narrowing the offer afterwards would orphan
 * the přihlášky teams already made against it.
 */
export function OfferCard({ competition }: { competition: Competition }) {
  const t = useT();
  const { data: workspace } = useRuleSet(competition.rule_set_id);
  const { data: offer } = useCompetitionOffer(competition.id);
  const updateOffer = useUpdateCompetitionOffer(competition.id);

  // Null until the organizer touches something: the server's answer is
  // the truth, a local set is the pending edit. That is also what makes
  // "nothing changed yet" a state the buttons can read.
  const [draftCodes, setDraftCodes] = useState<Set<string> | null>(null);
  const [draftDisciplines, setDraftDisciplines] = useState<Set<ID> | null>(null);

  const savedCodes = useMemo(
    () => new Set(offer?.excluded_class_codes ?? []),
    [offer?.excluded_class_codes],
  );
  const savedDisciplines = useMemo(
    () => new Set(offer?.excluded_discipline_ids ?? []),
    [offer?.excluded_discipline_ids],
  );
  const codes = draftCodes ?? savedCodes;
  const disciplines = draftDisciplines ?? savedDisciplines;
  const dirty = draftCodes !== null || draftDisciplines !== null;

  /** The distinct class codes, each with the name its rows carry. */
  const allCodes = useMemo(() => {
    const byCode = new Map<string, string>();
    for (const table of workspace?.performance_class_tables ?? []) {
      for (const c of table.classes ?? []) {
        if (!byCode.has(c.code)) byCode.set(c.code, c.name);
      }
    }
    return [...byCode.entries()]
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.code.localeCompare(b.code, "cs"));
  }, [workspace]);

  /** Disciplines in rules-book order, grouped by family. */
  const families = useMemo(() => {
    const groups = new Map<string, Discipline[]>();
    const ordered = [...(workspace?.disciplines ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "cs"),
    );
    for (const d of ordered) {
      groups.set(d.family, [...(groups.get(d.family) ?? []), d]);
    }
    return [...groups.entries()].map(([code, rows]) => ({
      code,
      name: familyName(workspace?.families, code),
      rows,
    }));
  }, [workspace]);

  const totalDisciplines = families.reduce((n, f) => n + f.rows.length, 0);
  const offeredDisciplines = totalDisciplines - disciplines.size;
  // The server's lock (a published round of the series), or a reader who
  // may change nothing (competitions_viewer).
  const readOnly = useCompetitionReadOnly();
  const locked = (offer?.locked ?? false) || readOnly;
  const sharedWith = offer && offer.competitions > 1 ? offer.competitions : 0;

  // Which families are unfolded. Null until the organizer touches a
  // fold, so the default can be derived once the offer has loaded —
  // the same "server answer or pending edit" shape as the drafts above.
  //
  // Collapsing is lossless here because every family header carries its
  // own count, so a folded card still says what runs. The families that
  // open by default are the ones with something switched off: those are
  // the exceptions, and an exception you cannot see is one you forget
  // you made.
  //
  // Derived from the SAVED offer rather than the pending edit: reading
  // the draft would fold and unfold groups underneath the organizer as
  // they tick boxes, and a panel that moves while you work in it is
  // worse than one that shows too much.
  const [openFamilies, setOpenFamilies] = useState<Set<string> | null>(null);
  const defaultOpen = useMemo(
    () =>
      new Set(
        families
          .filter((f) => f.rows.some((d) => savedDisciplines.has(d.id)))
          .map((f) => f.code),
      ),
    [families, savedDisciplines],
  );
  const open = openFamilies ?? defaultOpen;
  const allOpen = families.length > 0 && families.every((f) => open.has(f.code));

  const toggleFamily = (code: string) => {
    const next = new Set(open);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setOpenFamilies(next);
  };

  const foldAll = () =>
    setOpenFamilies(allOpen ? new Set() : new Set(families.map((f) => f.code)));

  const setExcluded = (ids: ID[], excluded: boolean) => {
    const next = new Set(disciplines);
    for (const id of ids) {
      if (excluded) next.add(id);
      else next.delete(id);
    }
    setDraftDisciplines(next);
  };

  const toggleCode = (code: string, offered: boolean) => {
    const next = new Set(codes);
    if (offered) next.delete(code);
    else next.add(code);
    setDraftCodes(next);
  };

  const reset = () => {
    setDraftCodes(null);
    setDraftDisciplines(null);
  };

  const save = async () => {
    try {
      await updateOffer.mutateAsync({
        excluded_class_codes: [...codes],
        excluded_discipline_ids: [...disciplines],
      });
      reset();
      notifySuccess("Vypsané disciplíny a třídy byly uloženy.");
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Card>
      <Card.Header title="Vypsané disciplíny a třídy" />
      <Card.Content>
        <div className={styles.body}>
          <Text variant="muted">
            Sada pravidel určuje, které kombinace smějí existovat; soutěž z nich vypisuje
            podmnožinu. Co odškrtnete, se na soutěži nepojede a týmy to v přihlášce neuvidí.{" "}
            {sharedWith > 0 && <strong>Nastavení platí pro celou sérii.</strong>}
          </Text>

          {locked && (
            <Alert variant="warn">
              Soutěž je zveřejněná, takže se vypsané disciplíny a třídy už nemění; týmy se
              podle nich přihlašují.
            </Alert>
          )}
          {updateOffer.error && <Alert variant="err">{apiErrorMessage(updateOffer.error, t)}</Alert>}

          <FormSectionLabel>Výkonnostní třídy</FormSectionLabel>
          {allCodes.length === 0 ? (
            <Text variant="muted">Sada pravidel nemá žádné výkonnostní třídy.</Text>
          ) : (
            // A row rather than a stack: there are two of them and they are
            // one decision read side by side, not a list to scan.
            <div className={styles.classRow}>
              {/* The code alone. „Výkonnostní třída A" under a box labelled
                  „A" is the label again in a longer form — and the lib's
                  Checkbox folds a hint into the control's accessible name,
                  so it made the announcement worse too. */}
              {allCodes.map(({ code }) => (
                <Checkbox
                  key={code}
                  label={code}
                  className={styles.classBox}
                  checked={!codes.has(code)}
                  disabled={locked}
                  onChange={(e) => toggleCode(code, e.target.checked)}
                />
              ))}
            </div>
          )}

          <FormSectionLabel className={styles.sectionLabel}>
            <span>Disciplíny</span>
            <span className={styles.sectionMeta}>
              <Badge variant={offeredDisciplines === 0 ? "warn" : "info"}>
                {offeredDisciplines} z {totalDisciplines}
              </Badge>
              {families.length > 1 && (
                <Button size="sm" weight="ghost" onClick={foldAll}>
                  {allOpen ? "Sbalit vše" : "Rozbalit vše"}
                </Button>
              )}
            </span>
          </FormSectionLabel>

          {families.length === 0 ? (
            <Text variant="muted">Sada pravidel nemá žádné disciplíny.</Text>
          ) : (
            families.map((family) => {
              const ids = family.rows.map((d) => d.id);
              const on = ids.filter((id) => !disciplines.has(id)).length;
              const allOn = on === ids.length;
              const expanded = open.has(family.code);
              return (
                // The open state stays ours (one Set for the whole card), so
                // this is the controlled form. `summary` keeps the count
                // readable while the section is shut, which is what makes
                // collapsing lossless — and `actions` renders the bulk toggle
                // OUTSIDE the disclosure button, so it is not an interactive
                // element nested inside another. That is what the hand-rolled
                // version needed a separate <header> row to achieve.
                <Accordion.Item
                  key={family.code}
                  title={<span className={styles.familyName}>{family.name}</span>}
                  summary={`${on} z ${ids.length}`}
                  open={expanded}
                  onOpenChange={() => toggleFamily(family.code)}
                  actions={
                    <Button
                      size="sm"
                      weight="ghost"
                      disabled={locked}
                      // Every family repeats this button, so the visible text
                      // alone would read as "Zrušit vše" four times over with
                      // nothing to tell them apart out loud.
                      aria-label={
                        allOn
                          ? `Zrušit celou skupinu ${family.name}`
                          : `Vypsat celou skupinu ${family.name}`
                      }
                      onClick={() => setExcluded(ids, allOn)}
                    >
                      {allOn ? "Zrušit vše" : "Vypsat vše"}
                    </Button>
                  }
                >
                  <ResponsiveGrid columns={2} gap="sm">
                    {family.rows.map((d) => (
                      <Checkbox
                        key={d.id}
                        label={d.name}
                        checked={!disciplines.has(d.id)}
                        disabled={locked}
                        onChange={(e) => setExcluded([d.id], !e.target.checked)}
                      />
                    ))}
                  </ResponsiveGrid>
                </Accordion.Item>
              );
            })
          )}

          <div className={styles.actions}>
            {dirty && (
              <Button onClick={reset} disabled={updateOffer.isPending}>
                Zahodit změny
              </Button>
            )}
            <Button
              variant="primary"
              loading={updateOffer.isPending}
              disabled={locked || !dirty}
              onClick={save}
            >
              Uložit
            </Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}
